import React, { useRef } from "react";
import { useIframePoolStore } from "./iframePoolStore";
import { useActiveCanvas } from "@/utils/utils";
import { useStoryboardUrl } from "../hooks/useStoryboardUrl";
import { useIframePostMessaging } from "./useIframePostMessaging";
import { useSelector } from "react-redux";
import { CursorType } from "@/store/globalSettingsSlice";
import { EXIT_FULL_SCREEN_OVERLAY_HEIGHT } from "./ExitFullScreenOverlay";

/**
 * Dedicated fullscreen iframe that renders at 100% width and height
 * Only rendered when a storyboard is in fullscreen mode
 */
export const FullscreenIframe: React.FC = () => {
  const fullscreenStoryboardId = useIframePoolStore(
    (state) => state.fullscreenStoryboardId,
  );

  const activeCanvas = useActiveCanvas();
  const storyboard = activeCanvas?.storyboards?.find(
    (s) => s.id === fullscreenStoryboardId,
  );

  const isPanelResizing = useIframePoolStore((state) => state.isPanelResizing);

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const isDriveMode = cursorType === CursorType.Drive;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Get the appropriate iframe info based on storyboard type
  const appStoryboard = useIframePoolStore((state) =>
    fullscreenStoryboardId
      ? state.applicationStoryboards[fullscreenStoryboardId]
      : null,
  );

  // For registered storyboards, use the single tempobook iframe
  const tempobookIframe = useIframePoolStore((state) => state.tempobookIframe);

  // Determine iframe ID based on type
  const iframeId = appStoryboard?.iframeId || tempobookIframe?.id || null;

  // Get the storyboard URL
  const storyboardUrl = useStoryboardUrl(storyboard);

  const { onLoadIFrame } = useIframePostMessaging();

  // Handle iframe load
  const handleLoad = async () => {
    if (!iframeRef.current?.contentWindow || !iframeId) {
      return;
    }

    // Set up the iframe connection
    if (appStoryboard && storyboard) {
      await onLoadIFrame(iframeRef.current.contentWindow, {
        storyboardId: storyboard.id,
        type: storyboard.type,
        fullFilePath: storyboard.fullFilePath || "",
        originalStoryboardUrl: "",
        iframeId: appStoryboard.iframeId,
      });
      // Also update the content window in the store
      useIframePoolStore
        .getState()
        .setApplicationStoryboardContentWindow(
          fullscreenStoryboardId!,
          iframeRef.current.contentWindow,
        );
    } else {
      // Regular storyboard - no iframeId needed
      await onLoadIFrame(iframeRef.current.contentWindow);
    }
  };

  if (!fullscreenStoryboardId || !storyboard) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        zIndex: 10000,
      }}
    >
      <iframe
        ref={iframeRef}
        id={`fullscreen-${iframeId}`}
        src={storyboardUrl}
        style={{
          position: "absolute",
          left: 0,
          top: `${EXIT_FULL_SCREEN_OVERLAY_HEIGHT}px`,
          width: "100%",
          height: `calc(100% - ${EXIT_FULL_SCREEN_OVERLAY_HEIGHT}px)`,
          border: "none",
          pointerEvents: isDriveMode && !isPanelResizing ? "auto" : "none",
        }}
        onLoad={handleLoad}
        className="bg-white"
      />
    </div>
  );
};
